import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {UserService} from "../service/user.service";
import {Router} from "@angular/router";
import {
  CONFIRM_PASSWORD,
  PASSWORD,
  PASSWORD_LOWERCASE_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_PATTERN,
  PASSWORD_UPPERCASE_REGEX
} from "../constants/passwordValidation.constants";
import {SharedConstants} from "../utilityClass/sharedConstants";

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss', '../registration/registration.component.scss', '../forgot-password/forgot-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {

  @ViewChild('confirmPasswordInput') confirmPasswordInput!: ElementRef;
  @ViewChild('passwordInput') passwordInput!: ElementRef;

  changePasswordForm: FormGroup;
  form: FormGroup | any

  showPassword;
  showConfirmPassword;

  token: string;
  email: string;
  reservedIdentificationId: string | null = null;
  returnUrl: string | null = null;

  criteriaList: string[] = ['minlength', 'maxlength', 'lowercase', 'uppercase'];

  constructor(private formBuilder: FormBuilder, private userService: UserService, private router: Router) {
    this.token = this.router.getCurrentNavigation()?.extras.state?.['token'];
    this.email = router.getCurrentNavigation()?.extras.state?.['email'];
    this.validateToken(this.token);
    this.changePasswordForm = this.createForgotForm();
    this.showPassword = false;
    this.showConfirmPassword = false;
  }

  ngOnInit() {
    this.reservedIdentificationId = history.state.reservedIdentificationId || null;
    this.returnUrl = history.state.returnUrl || null;
  }

  createForgotForm(): FormGroup {
    return this.formBuilder.group({
      password: [null, [
        Validators.required,
        Validators.minLength(PASSWORD_MIN_LENGTH),
        Validators.maxLength(PASSWORD_MAX_LENGTH),
        Validators.pattern(PASSWORD_PATTERN)]],
      confirmPassword: [null, Validators.required]
    }, {updateOn: 'blur'});
  }

  sendResetLink() {
    this.changePasswordForm.markAllAsTouched();
    if (this.changePasswordForm.invalid) {
      return;
    }
    const {password, confirmPassword} = this.changePasswordForm.value;
    this.userService.resetPassword(password, confirmPassword, this.token).subscribe({
      next: () => this.router.navigate(['login'], {
        state: {
          email: this.email,
          reservedIdentificationId: this.reservedIdentificationId,
          returnUrl: this.returnUrl
        },
        replaceUrl: true
      }),
      error: () => this.router.navigate(['error-page'], {replaceUrl: true}),
    });
  }

  onInputSpaceChange(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const cleanValueWithoutSpaces = input.value.replace(/\s/g, '');
    this.changePasswordFormControl[controlName].setValue(cleanValueWithoutSpaces);
  }

  passwordMatchValidator(control: AbstractControl): { passwordMismatch: boolean } | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return password !== confirmPassword ? {passwordMismatch: true} : null;
  }

  isPasswordValid(criteria: string): boolean {
    const password = this.changePasswordFormControl['password'].value;

    switch (criteria) {
      case 'minlength':
        return password.length >= PASSWORD_MIN_LENGTH;
      case 'maxlength':
        return password.length <= PASSWORD_MAX_LENGTH;
      case 'lowercase':
        return PASSWORD_LOWERCASE_REGEX.test(password);
      case 'uppercase':
        return PASSWORD_UPPERCASE_REGEX.test(password);
      default:
        return false;
    }
  }

  togglePasswordVisibility(field: string): void {
    if (field === PASSWORD) {
      this.showPassword = !this.showPassword;
      this.updateInputType(this.passwordInput.nativeElement, this.showPassword);
    } else if (field === CONFIRM_PASSWORD) {
      this.showConfirmPassword = !this.showConfirmPassword;
      this.updateInputType(this.confirmPasswordInput.nativeElement, this.showConfirmPassword);
    }
  }

  private updateInputType(inputElement: HTMLInputElement, show: boolean): void {
    const fieldType = show ? 'text' : 'password';
    inputElement.type = fieldType;
  }

  getCriteriaText(criteria: string): string {
    const criteriaText: { [key: string]: string } = {
      minlength: '8 characters minimum',
      maxlength: '20 characters maximum',
      lowercase: 'lowercase letter',
      uppercase: 'uppercase letter'
    };
    return criteriaText[criteria] || '';
  }

  validateToken(token: string): void {
    if (!token) {
      this.router.navigate([`error-page`])
    }
  }

  get changePasswordFormControl() {
    return this.changePasswordForm.controls;
  }

  protected readonly SharedConstants = SharedConstants;
}
